import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import SEO from '../../components/SEO/SEO';

const Pricing: React.FC = () => {
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "Service",
    "name": "HR Services & Pricing - ERHR Global",
    "description": "Transparent HR consulting pricing from ERHR Global. HR Risk & Readiness Assessment, HR Foundation Build, and Fractional HR Partner plans for growing businesses.",
    "url": "https://erhrglobal.com/pricing",
    "provider": {
      "@type": "ProfessionalService",
      "name": "ERHR Global",
      "url": "https://erhrglobal.com",
      "address": {
        "@type": "PostalAddress",
        "addressLocality": "Boston",
        "addressRegion": "MA",
        "addressCountry": "US"
      }
    },
    "areaServed": [{"@type": "City", "name": "Boston"}, {"@type": "State", "name": "Massachusetts"}],
    "hasOfferCatalog": {
      "@type": "OfferCatalog",
      "name": "HR Services & Pricing",
      "itemListElement": [
        {
          "@type": "Offer",
          "price": "5000",
          "priceCurrency": "USD",
          "itemOffered": {"@type": "Service", "name": "HR Risk & Readiness Assessment"}
        },
        {
          "@type": "Offer",
          "price": "8500",
          "priceCurrency": "USD",
          "itemOffered": {"@type": "Service", "name": "HR Foundation Build"}
        },
        {
          "@type": "Offer",
          "price": "15000",
          "priceCurrency": "USD",
          "itemOffered": {"@type": "Service", "name": "Fractional HR Partner"}
        }
      ]
    }
  };

  const plans = [
    {
      title: "HR Risk & Readiness Assessment",
      price: "$5,000",
      meta: "Flat fee · 2-3 weeks",
      description: "A focused diagnostic for leaders who want to understand where HR risk, capacity gaps, and process weaknesses exist before deciding what to fix.",
      features: [
        "12-point HR risk review",
        "HR capacity map",
        "Prioritized Risk-to-Readiness report",
        "30-60-90 action roadmap",
        "Leadership findings walkthrough"
      ],
      bestFor: "Best for companies that need clarity before committing to a larger HR project."
    },
    {
      title: "HR Foundation Build",
      price: "$8,500",
      meta: "Fixed scope · 6-10 weeks",
      description: "A defined implementation project that closes the highest-priority HR gaps and leaves the business with practical, documented systems.",
      features: [
        "Risk & Readiness work included",
        "Policies, handbook and manager workflows as in scope",
        "Onboarding and offboarding process design",
        "Compliance and documentation remediation as in scope",
        "Handover plus two post-delivery checkpoints"
      ],
      bestFor: "Best for growing companies with a known HR gap, deadline, audit, expansion, or scaling need."
    },
    {
      title: "Fractional HR Partner",
      price: "$15,000 setup",
      meta: "+ $3,500-$6,000/month",
      description: "Ongoing senior HR coverage for companies that need experienced HR leadership without adding a full-time HR Director.",
      features: [
        "Foundation build or applicable Build credit",
        "Ongoing HR guidance and risk monitoring",
        "Named point of contact for people issues",
        "Quarterly Risk-to-Readiness reviews",
        "Compliance upkeep and priority access"
      ],
      bestFor: "$3,500 under 100 employees · $4,750 for 100-300 · $6,000 for 300+"
    }
  ];

  const mechanics = [
    {
      title: "Assessment credit",
      description: "100% of the $5,000 Assessment fee is credited toward a Foundation Build started within 30 days."
    },
    {
      title: "Build-to-retainer credit",
      description: "A completed Foundation Build can be credited toward the Fractional HR Partner setup."
    },
    {
      title: "Annual retainer",
      description: "Annual prepayment receives two months free."
    }
  ];

  return (
    <div className="min-h-screen">
      <SEO
        title="HR Services & Pricing - ERHR Global"
        description="ERHR Global pricing: HR Risk Assessment ($5K), Foundation Build ($8.5K), and Fractional HR Partner plans. Transparent, flexible engagement."
        url="https://erhrglobal.com/pricing"
        structuredData={structuredData}
      />

      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-primary-600 to-primary-800 text-white py-20 overflow-hidden">
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center"
          >
            <p className="text-sm font-bold tracking-widest text-primary-200 uppercase mb-4">ERHR Global</p>
            <h1 className="text-4xl lg:text-5xl font-bold mb-6">HR support that meets your business where it is now.</h1>
            <p className="text-xl text-primary-100 max-w-3xl mx-auto">
              Start with clarity, build the right foundation, or add ongoing senior HR support without committing to a full-time HR Director.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {plans.map((plan, index) => (
              <motion.div
                key={plan.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-shadow duration-300 border border-gray-200 flex flex-col"
              >
                <h2 className="text-xl font-semibold text-gray-900 mb-2">{plan.title}</h2>
                <div className="text-3xl font-bold text-primary-600 mb-1">{plan.price}</div>
                <div className="text-sm font-semibold text-gray-500 mb-4">{plan.meta}</div>
                <p className="text-gray-600 mb-6">{plan.description}</p>
                <ul className="space-y-3 mb-6">
                  {plan.features.map((feature) => (
                    <li key={feature} className="flex items-start">
                      <svg className="w-5 h-5 text-primary-600 mr-3 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                      <span className="text-gray-600 text-sm">{feature}</span>
                    </li>
                  ))}
                </ul>
                <div className="mt-auto pt-4 border-t border-gray-200 text-sm font-semibold text-gray-900">
                  {plan.bestFor}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Mechanics Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl font-bold text-gray-900 mb-4">A clear path from assessment to ongoing support</h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {mechanics.map((item, index) => (
              <motion.div
                key={item.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-shadow duration-300"
              >
                <h3 className="text-xl font-semibold text-primary-600 mb-4">{item.title}</h3>
                <p className="text-gray-600">{item.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Services covered */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-8 text-center">Areas we cover across every engagement</h2>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {[
              { label: "Transitional HR", path: "/transitional-hr" },
              { label: "Employee Engagement", path: "/strategic-hr/employee-engagement" },
              { label: "Performance Management", path: "/strategic-hr/performance-management" },
              { label: "HR Systems", path: "/strategic-hr/hr-systems" },
              { label: "Benefits Strategy", path: "/strategic-hr/benefits-strategy" },
              { label: "Policies & Procedures", path: "/strategic-hr/policies-procedures" },
              { label: "Onboarding & Offboarding", path: "/strategic-hr/onboarding-offboarding" },
              { label: "About ERHR", path: "/who-we-are" },
            ].map((item) => (
              <Link
                key={item.path}
                to={item.path}
                className="bg-gray-50 hover:bg-primary-50 rounded-lg p-4 text-center text-sm font-medium text-gray-700 hover:text-primary-700 transition-colors duration-200"
              >
                {item.label}
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-primary-600">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <h2 className="text-4xl font-bold text-white mb-4">Not sure where to start?</h2>
            <p className="text-xl text-primary-100 mb-8 max-w-3xl mx-auto">
              Start with the level of HR support your business needs now and scale from there.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/schedule-appointment"
                className="bg-white text-primary-700 px-8 py-4 rounded-lg font-semibold hover:bg-primary-50 transition-colors duration-300"
              >
                Discuss Your HR Needs
              </Link>
              <Link
                to="/contact"
                className="border-2 border-white text-white px-8 py-4 rounded-lg font-semibold hover:bg-white hover:text-primary-700 transition-colors duration-300"
              >
                Contact Us
              </Link>
            </div>
            <p className="text-sm text-primary-200 mt-6">Final scope and pricing are confirmed before engagement begins.</p>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Pricing;
